import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';

import {
    useActivateNAVMutation,
    useGetNAVQuery,
    useGetShareSubcategoriesQuery,
    useTestNAVMutation,
    useSimulateNAVMutation,
    useCreateNAVMutation,
    useUpdateNAVMutation,
} from 'api/main';
import { EURO_SYMBOL, billStatus as NAVStatus } from 'utils/constants';
import { getShareSubcategoryIdFromIRI } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import CapitalAccountForm from 'containers/capital-accounts/CapitalAccountForm';
import { MenuActivate, MenuTest, MenuSimulate } from 'components/ActionsMenu/actions';
import { ActionsMenu, ConfirmDialog, CustomSelect, ListContainer, Loader } from 'components';
import {
    Box,
    Breadcrumbs,
    Link,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import * as icons from 'mdi-material-ui';

const tableHead = (
    <TableHead>
        <TableRow>
            <TableCell width="8%">{texts.CATEGORIE}</TableCell>
            <TableCell>VL ({EURO_SYMBOL})</TableCell>
            <TableCell>{texts.DATE}</TableCell>
        </TableRow>
    </TableHead>
);

const ActivateActionItem = React.forwardRef((props, ref) => <MenuActivate ref={ref} {...props} />);
const TestActionItem = React.forwardRef((props, ref) => <MenuTest ref={ref} {...props} />);
const SimulateActionItem = React.forwardRef((props, ref) => <MenuSimulate ref={ref} {...props} />);

const VLManagement = ({ fund }) => {
    const { data: navs = [], isFetching: dataLoading } = useGetNAVQuery({
        'shareSubcategory.shareCategory.investmentFund': fund?.id,
        pagination: false,
    });

    const [createNAV, { isLoading: createLoading }] = useCreateNAVMutation();
    const [updateNAV, { isLoading: updateLoading }] = useUpdateNAVMutation();
    const [testNAV, { isLoading: testLoading }] = useTestNAVMutation();
    const [simulateNAV, { isLoading: simulateLoading }] = useSimulateNAVMutation();
    const [activateNAV, { isLoading: activateLoading, isSuccess: activateSuccess }] =
        useActivateNAVMutation();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery({
            displayUnavailable: 1,
            'shareCategory.investmentFund': fund?.id,
        });

    const [selectedDate, setSelectedDate] = useState('');
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);

    const createdNAVs = useMemo(() => navs.filter(ca => ca.status === NAVStatus.CREATED), [navs]);

    const activatedNAVs = useMemo(
        () => navs.filter(ca => ca.status === NAVStatus.ACTIVATED),
        [navs]
    );

    const completedNAVs = useMemo(
        () => navs.filter(ca => ca.status === NAVStatus.COMPLETED),
        [navs]
    );

    const activationDates = useMemo(() => {
        const dates = [...activatedNAVs, ...completedNAVs].map(ca => ca.date);
        return [...new Set(dates)];
    }, [activatedNAVs, completedNAVs]);

    const handleActivate = useCallback(() => {
        activateNAV(fund?.id);
    }, [fund?.id, activateNAV]);

    const handleTest = useCallback(() => {
        testNAV(fund?.id);
    }, [fund?.id, testNAV]);

    const handleSimulate = useCallback(() => {
        simulateNAV(fund?.id);
    }, [fund?.id, simulateNAV]);

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
        }
    }, [activateSuccess]);

    useEffect(() => {
        if (activationDates.length) {
            setSelectedDate(activationDates[0]);
        }
    }, [activationDates]);

    return (
        <ListContainer>
            <Breadcrumbs separator={<icons.MenuRight />} aria-label="breadcrumb">
                <Link
                    underline="hover"
                    href="/fund-management"
                    to="/fund-management"
                    component={RouterLink}
                >
                    {texts.FUND}
                </Link>
                <Link
                    underline="hover"
                    href={`/fund-management/${fund?.id}/attributes`}
                    to={`/fund-management/${fund?.id}/attributes`}
                    component={RouterLink}
                >
                    {texts.VL_MANAGEMENT} - {fund?.name}
                </Link>
            </Breadcrumbs>
            <ConfirmDialog
                open={activateDialogOpen}
                title={texts.CAPITAL_ACCOUNT_ACTIVATION}
                cancelText={texts.CANCEL}
                confirmText={texts.CONFIRMER}
                loading={activateLoading}
                handleClose={() => setActivateDialogOpen(false)}
                handleConfirm={handleActivate}
            >
                {texts.CONFIRM_CAPITAL_ACCOUNT_ACTIVATION}
            </ConfirmDialog>
            <Box marginY={3} textAlign="center">
                <Loader loading={subcategoriesLoader || dataLoading}>
                    <CapitalAccountForm
                        vl
                        activationDates={activationDates}
                        shareSubcategories={shareSubcategories}
                        tableHead={tableHead}
                        createdList={createdNAVs}
                        completedList={completedNAVs}
                        dataLoading={dataLoading}
                        onAdd={createNAV}
                        onUpdate={updateNAV}
                        loading={createLoading || updateLoading}
                    />
                    <Box paddingTop={4}>
                        {!!createdNAVs.length && (
                            <ActionsMenu
                                autoClose
                                justify="center"
                                buttonStyle="text"
                                buttonText={texts.VL_MANAGEMENT}
                            >
                                <ActivateActionItem
                                    disabled={testLoading}
                                    onClick={() => setActivateDialogOpen(true)}
                                />
                                <TestActionItem disabled={testLoading} onClick={handleTest} />
                                <SimulateActionItem
                                    disabled={simulateLoading}
                                    onClick={handleSimulate}
                                />
                            </ActionsMenu>
                        )}
                    </Box>
                    {!!activationDates.length && (
                        <React.Fragment>
                            <Box paddingTop={4} justifyContent="center" display="flex">
                                <CustomSelect
                                    value={selectedDate}
                                    onChange={e => setSelectedDate(e.target.value)}
                                    label={texts.VL_HISTORY}
                                >
                                    {activationDates.map(date => (
                                        <MenuItem key={date} value={date}>
                                            {moment(date).format('L')}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </Box>
                            <TableContainer>
                                <Table>
                                    {tableHead}
                                    <TableBody>
                                        {[...activatedNAVs, ...completedNAVs]
                                            .filter(ca =>
                                                moment(ca.date).isSame(moment(selectedDate), 'day')
                                            )
                                            .map((ca, i) => (
                                                <TableRow key={ca.id}>
                                                    <TableCell>
                                                        {
                                                            shareSubcategories.find(
                                                                sc =>
                                                                    sc.id ===
                                                                    getShareSubcategoryIdFromIRI(
                                                                        ca.shareSubcategory
                                                                    )
                                                            )?.name
                                                        }
                                                    </TableCell>
                                                    <TableCell>{ca.vl}</TableCell>
                                                    {i === 0 && (
                                                        <TableCell rowSpan={7}>
                                                            {moment(ca.date).format('L')}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </React.Fragment>
                    )}
                </Loader>
            </Box>
        </ListContainer>
    );
};

VLManagement.propTypes = { fund: PropTypes.object };

export default VLManagement;
